/**
 * Canonical (shareable) links.
 *
 * Since the per-tab account moved out of the URL and into `sessionStorage`, an
 * in-app URL names ONLY a resource — which makes the address bar itself already
 * canonical. This module is the single place that states that contract, so the
 * two things that must never diverge stay together:
 *
 *   • {@link canonicalUrl} — what a "Copy link" affordance hands the user. It
 *     strips the one-shot `?as=` handoff, which is the ONLY account-bearing
 *     parameter the app ever puts in a URL and which is meaningless (and inert)
 *     on any other device.
 *   • {@link accountHandoffUrl} — the deliberately NON-canonical form, used
 *     only to carry this tab's account into a NEW TAB of the SAME browser.
 *
 * The asymmetry is the point: sharing must never leak an account coordinate,
 * while opening a second tab of your own must preserve one.
 */

import { aliasForAccount } from "@/lib/account-alias";
import { ACCOUNT_HANDOFF_PARAM } from "@/lib/tab-account";

/**
 * The current page's shareable URL: absolute, with the handoff parameter
 * removed. Everything else (path + search + hash) is preserved, because the
 * whole point of the URL-addressability work is that those DO reproduce the
 * screen on another device.
 */
export function canonicalUrl(href: string = globalThis.location.href): string {
  try {
    const url = new URL(href);
    url.searchParams.delete(ACCOUNT_HANDOFF_PARAM);
    return url.toString();
  } catch {
    return href;
  }
}

/**
 * The same URL decorated with THIS tab's account handoff, for opening in a new
 * tab of the same browser. The alias is device-local and opaque; on any other
 * device it resolves to nothing and the parameter is simply dropped.
 *
 * Returns the canonical URL unchanged when there is no account to carry.
 */
export function accountHandoffUrl(href: string, userId: string | null): string {
  if (userId === null) {
    return canonicalUrl(href);
  }
  try {
    const url = new URL(canonicalUrl(href));
    url.searchParams.set(ACCOUNT_HANDOFF_PARAM, aliasForAccount(userId));
    return url.toString();
  } catch {
    return href;
  }
}
